/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Link } from 'react-router-dom'

import { Alert, PageContainer, PageHeader } from '@/components/ui'
import { useApi } from '@/hooks'

import { MigrationFiltersBar } from './MigrationFiltersBar'
import { LastRun } from './RunHistory'
import { comparePath, missingFilter, runMigration } from './runMigration'
import { MatchBadge } from './RunSummary'
import { JiraLink, StatusBadge } from './StatusBadge'
import { MIGRATION_STATUSES, type MigrationItem, type RunResult } from './types'
import { useMigrationFilters } from './useMigrationFilters'

import { AdminHeaderCells, AdminRowCells, type AdminColumns, useAdminTable } from '../adminTable'
import { LoadingPanel } from '../LoadingPanel'

interface RunState {
  running?: boolean
  result?: RunResult
  error?: string
}

/**
 * Fresh → Timescale migration at a glance: every endpoint with its status, Jira ticket and a
 * one-click comparison of both back ends, and the ingest issues with their SQL pairs.
 */
// Shared sort keys: status in workflow order, last run by when it ran (never-run last).
const statusOrder = (item: MigrationItem) => MIGRATION_STATUSES.indexOf(item.status)
const lastRunAt = (item: MigrationItem) => (item.lastRun ? new Date(item.lastRun.ranAt).getTime() : undefined)

const MigrationDashboardPage: React.FC = () => {
  const { api } = useApi()
  const { filters, setFilters, search, loadingDefaults } = useMigrationFilters()
  const [items, setItems] = useState<MigrationItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [runs, setRuns] = useState<Record<string, RunState>>({})

  useEffect(() => {
    const load = async () => {
      const res = await api.get<MigrationItem[]>({
        path: '/app/admin/migration/items',
        origin: 'MigrationDashboard#load',
      })
      setIsLoading(false)
      if (res.error || !res.data) {
        setError(res.message || 'Could not load the migration items')
        return
      }
      setItems(res.data)
    }
    void load()
  }, [api])

  const runTest = useCallback(
    async (item: MigrationItem) => {
      setRuns((r) => ({ ...r, [item.id]: { running: true } }))
      const { result, error: runError } = await runMigration(api, item.id, { kind: 'test' }, filters)
      setRuns((r) => ({ ...r, [item.id]: { result, error: runError } }))
      // The run was recorded: show it as the item's last run.
      if (result?.run) {
        const run = result.run
        setItems((list) => list.map((i) => (i.id === item.id ? { ...i, lastRun: run } : i)))
      }
    },
    [api, filters]
  )

  const endpoints = useMemo(() => items.filter((i) => i.kind === 'endpoint'), [items])
  const issues = useMemo(() => items.filter((i) => i.kind === 'issue'), [items])
  const itemLink = useCallback((id: string) => `/admin/migration/${encodeURIComponent(id)}?${search}`, [search])

  const endpointColumns = useMemo<AdminColumns<MigrationItem>>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorFn: (item) => item.name,
        cell: ({ row }) => (
          <Link to={itemLink(row.original.id)} className="link link-hover font-medium">
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'path',
        header: 'Path',
        accessorFn: (item) => item.path ?? undefined,
        meta: { className: 'font-mono text-xs' },
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: statusOrder,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: 'jira',
        header: 'Jira',
        accessorFn: (item) => item.jiraKey ?? undefined,
        cell: ({ row }) => <JiraLink jiraKey={row.original.jiraKey} />,
      },
      {
        id: 'lastRun',
        header: 'Last run',
        accessorFn: lastRunAt,
        cell: ({ row }) => <LastRun run={row.original.lastRun} />,
      },
      {
        id: 'test',
        header: 'Test',
        enableSorting: false,
        cell: ({ row }) => {
          const item = row.original
          if (item.status === 'todo') return null
          const run = runs[item.id] ?? {}
          const missing = missingFilter(filters, item.needs)
          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn btn-xs"
                disabled={run.running || Boolean(missing)}
                title={missing}
                onClick={() => void runTest(item)}
              >
                Test
              </button>
              <MatchBadge result={run.result} running={run.running} />
              {run.result?.compare && !run.result.compare.match && (
                <Link
                  to={comparePath(item.id, { kind: 'test' }, search)}
                  state={{ result: run.result }}
                  className="link text-xs"
                >
                  details
                </Link>
              )}
              {run.error && <span className="text-xs text-error">{run.error}</span>}
            </div>
          )
        },
      },
    ],
    [itemLink, runs, filters, runTest, search]
  )

  const issueColumns = useMemo<AdminColumns<MigrationItem>>(
    () => [
      {
        id: 'ticket',
        header: 'Ticket',
        accessorFn: (item) => item.ticket ?? undefined,
        meta: { className: 'font-mono text-xs' },
      },
      {
        id: 'issue',
        header: 'Issue',
        accessorFn: (item) => item.name,
        cell: ({ row }) => (
          <>
            <Link to={itemLink(row.original.id)} className="link link-hover font-medium">
              {row.original.name}
            </Link>
            {row.original.description && <div className="text-xs text-base-content/60">{row.original.description}</div>}
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: statusOrder,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: 'jira',
        header: 'Jira',
        accessorFn: (item) => item.jiraKey ?? item.ticket ?? undefined,
        cell: ({ row }) => <JiraLink jiraKey={row.original.jiraKey ?? row.original.ticket} />,
      },
      {
        id: 'lastRun',
        header: 'Last run',
        accessorFn: lastRunAt,
        cell: ({ row }) => <LastRun run={row.original.lastRun} />,
      },
      { id: 'sqlPairs', header: 'SQL pairs', accessorFn: (item) => item.sqlCount },
    ],
    [itemLink]
  )

  const endpointTable = useAdminTable(endpoints, endpointColumns, { getRowId: (item: MigrationItem) => item.id })
  const issueTable = useAdminTable(issues, issueColumns, { getRowId: (item: MigrationItem) => item.id })

  return (
    <PageContainer>
      <Link to="/admin" className="link text-sm">
        ← Admin
      </Link>
      <PageHeader title="Migration Dashboard" subtitle="Fresh → Timescale parity, endpoint by endpoint" />
      <MigrationFiltersBar filters={filters} onChange={setFilters} />
      {loadingDefaults && <p className="mt-2 text-sm text-base-content/60">Finding an asset with data…</p>}
      {error && <Alert message={error} type="error" />}
      {isLoading ? (
        <LoadingPanel label="Loading endpoints…" />
      ) : (
        <>
          <h2 className="mt-6 mb-2 text-lg font-semibold">Endpoints</h2>
          <div className="overflow-x-auto rounded-box bg-base-100 shadow">
            <table className="table table-sm table-zebra" data-testid="migration-endpoints">
              <thead>
                <tr>
                  <AdminHeaderCells table={endpointTable} />
                </tr>
              </thead>
              <tbody>
                {endpointTable.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    <AdminRowCells row={row} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="mt-6 mb-2 text-lg font-semibold">Data issues</h2>
          <div className="overflow-x-auto rounded-box bg-base-100 shadow">
            <table className="table table-sm table-zebra" data-testid="migration-issues">
              <thead>
                <tr>
                  <AdminHeaderCells table={issueTable} />
                </tr>
              </thead>
              <tbody>
                {issueTable.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    <AdminRowCells row={row} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageContainer>
  )
}

export default MigrationDashboardPage
